import * as React from 'react';
import { IPageName } from '../../types/apiSchema';
import {
  ElementName,
  ElementType,
  ErrorKey,
  ErrorType,
  IContentElementProps,
  InteractionType,
  ISaleListDisplayedDetails,
  ItemDetailsType,
  RenderedItemDetailsType,
} from './tracking.interfaces';

/**
 * Sends a tracking event about a component being rendered
 * https://github.com/secretescapes/front-end-tracking/blob/master/documentation/developer/event%20registry.md#content-rendered
 * @param contentElement Name and type of the element rendered
 * @param renderedItemDetails additional information about the item that was rendered
 */
export function trackContentRendered(
  contentElement: IContentElementProps,
  renderedItemDetails?: RenderedItemDetailsType
) {
  const trackingEvent: CustomEvent = new CustomEvent('contentRendered', {
    detail: {
      contentElement: {
        ...contentElement,
        ...renderedItemDetails,
      },
    },
  });

  dispatchEvent(trackingEvent);
}

/**
 * Sends a tracking event when content interaction is detected
 * https://github.com/secretescapes/front-end-tracking/blob/master/documentation/developer/event%20registry.md#content-interaction
 *
 * @param contentElement Identifies the element by name and type
 * @param interactionType what happened (e.g. click, scroll, etc)
 * @param itemDetails additional information about the item that was interacted with
 */
export function trackContentInteraction(
  contentElement: IContentElementProps,
  interactionType: InteractionType,
  itemDetails?: ItemDetailsType
) {
  const trackingEvent: CustomEvent = new CustomEvent('contentInteraction', {
    detail: {
      contentElement: {
        ...contentElement,
        interactionType,
        itemDetails,
      },
    },
  });

  dispatchEvent(trackingEvent);
}

/**
 * Sends a tracking event when content scrolled into the users viewport
 *
 * @param contentElement Identifies the element by name and type
 * @param itemDetails additional information about the item that was viewed
 */
export function trackContentViewed(
  contentElement: IContentElementProps,
  itemDetails?: ItemDetailsType
) {
  const trackingEvent: CustomEvent = new CustomEvent('contentViewed', {
    detail: {
      contentElement: {
        ...contentElement,
        itemDetails,
      },
    },
  });

  dispatchEvent(trackingEvent);
}

/**
 * Sends a tracking event about if the sale list has been displayed
 * https://github.com/secretescapes/analytics-tracking/blob/master/documentation/developer/aberrations/recommendations.md
 * @param contentElement Identifies the element by name and type
 * @param saleListDetails Number of results, search details including additional recommendation matches
 */
export function trackSaleListDisplayed(
  contentElement: IContentElementProps,
  saleListDetails: ISaleListDisplayedDetails
) {
  const trackingEvent: CustomEvent = new CustomEvent('saleListDisplayed', {
    detail: {
      contentElement: {
        ...contentElement,
        ...saleListDetails,
      },
    },
  });

  dispatchEvent(trackingEvent);
}

/**
 * Sends a error event to track issues on the frontend
 *
 * @param errorKey Identifies the error
 * @param errorType Generic type of error
 * @param errorMessage Error message that occured
 * @param errorObj any js object, e.g. caught exception
 */
export function trackError(
  errorKey: ErrorKey,
  errorType: ErrorType,
  errorMessage: string,
  errorObj?: object
) {
  // tslint:disable-next-line: no-console
  console.error(`${errorType}>${errorKey}: ${errorMessage}`, errorObj);

  const trackingEvent: CustomEvent = new CustomEvent('trackedError', {
    detail: {
      error: errorObj,
      key: errorKey,
      message: errorMessage,
      type: errorType,
    },
  });

  dispatchEvent(trackingEvent);
}

const dispatchEvent: (trackingEvent: CustomEvent) => void = trackingEvent => {
  window.dispatchEvent(trackingEvent);
};

/**
 * Hook to send an error event only when the error object changes
 *
 * @param errorKey Identifies the error
 * @param errorType Generic type of error
 * @param errorObj any js object with a message, e.g. caught exception
 */
export function useErrorTracking(
  errorKey: ErrorKey,
  errorType: ErrorType,
  error?: { message: string }
) {
  React.useEffect(() => {
    if (error) {
      trackError(errorKey, errorType, error.message, error);
    }
  }, [error, errorKey, errorType]);
}

const VIEW_TRACKING_VISIBLE_THRESHOLD: number = 0.3;
/**
 * Hook to send a tracking event when the object is at least 30% displayed in the viewport
 *
 * @param elementRef the reference to the element on the page which should be tracked
 * @param contentElement Identifies the element by name and type
 * @param itemDetails additional information about the item that was viewed
 */
export function useContentViewedTracking(
  elementRef: React.MutableRefObject<HTMLElement>,
  contentElement: IContentElementProps,
  itemDetails?: ItemDetailsType
) {
  React.useEffect(() => {
    // feature detection as IntersectionObserver isn't supported in IE
    if (!('IntersectionObserver' in window)) {
      return () => null;
    }
    const observer: IntersectionObserver = new IntersectionObserver(
      entries => {
        const ratio: number = entries[0].intersectionRatio;
        if (ratio > VIEW_TRACKING_VISIBLE_THRESHOLD) {
          trackContentViewed(contentElement, itemDetails);
        }
      },
      { threshold: VIEW_TRACKING_VISIBLE_THRESHOLD }
    );

    observer.observe(elementRef.current);

    return () => {
      observer.disconnect();
    };
  }, [itemDetails, contentElement.elementType, contentElement.elementName, elementRef]);
}

export const trackingIdMap: { [k in IPageName]?: IContentElementProps } = {
  [IPageName.collections]: {
    elementName: ElementName.COLLECTION,
    elementType: ElementType.COLLECTION,
  },
  [IPageName.currentSales]: {
    elementName: ElementName.CURRENT_SALES,
    elementType: ElementType.HOMEPAGE_PANEL,
  },
  [IPageName.searchResults]: {
    elementName: ElementName.SEARCH_RESULTS,
    elementType: ElementType.SEARCH_RESULTS,
  },
};
